import { useMutation, useQueryClient } from '@tanstack/react-query';
import { withholdingsQueryKey } from './keys';
import { normalizeWithholding, sortWithholdings } from '../types';
import type {
  RawWithholdingDto,
  RecalculateSummary,
  RecalculateWithholdingsResponse,
  WithholdingDto,
  WithholdingsHttpRequest,
} from '../types';

export interface UseRecalculateWithholdingsParams {
  documentId: string;
  /** Endpoint base de child (`/{id}/withholdings`); se le añade `/recalculate`. */
  withholdingsBaseEndpoint: string;
  httpRequest: WithholdingsHttpRequest;
}

interface RawRecalculateResponse {
  withholdings?: RawWithholdingDto[];
  summary?: Partial<RecalculateSummary>;
}

/**
 * Recalcula las retenciones (`POST /{id}/withholdings/recalculate`) → 200
 * `{ withholdings, summary }`.
 *
 * El motor (LEGO-13) borra las automáticas, preserva las manuales y restaura las
 * manipuladas con los valores del usuario. Devuelve `{ preserved, recalculated }`.
 *
 * - `onSuccess`: siembra el cache con el set TF3 completo regenerado.
 * - `onSettled`: invalida `['document-withholdings', documentId]`.
 */
export function useRecalculateWithholdings({
  documentId,
  withholdingsBaseEndpoint,
  httpRequest,
}: UseRecalculateWithholdingsParams) {
  const queryClient = useQueryClient();
  const key = withholdingsQueryKey(documentId);

  return useMutation<RecalculateWithholdingsResponse, Error, void>({
    mutationFn: async () => {
      const res = await httpRequest<RawRecalculateResponse>(
        'POST',
        `${withholdingsBaseEndpoint}/recalculate`,
      );
      return {
        withholdings: sortWithholdings((res.withholdings ?? []).map(normalizeWithholding)),
        summary: {
          preserved: Number(res.summary?.preserved ?? 0),
          recalculated: Number(res.summary?.recalculated ?? 0),
        },
      };
    },
    onSuccess: (result) => {
      queryClient.setQueryData<WithholdingDto[]>(key, result.withholdings);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
